import css from './Tap2GOMultipleModal.module.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import axios from 'axios'
import classNames from 'classnames'
import { ObjectGroupBy } from 'object-group-bye'
import { GoodTicketCode } from 'types/tickets'
import { v4 as uuidv4 } from 'uuid'

import DataTestId from 'meta/dataTestId'
import { IGoodServiceTicketData, ITap2GOData, Tap2GOStatus } from 'meta/pages/goodsandservices'
import { ApiEndpoint } from 'controller/endpoint'
import {
  TICKETSMS_COLORS_LIGHTGREY,
  TICKETSMS_COLORS_LILA,
  TICKETSMS_COLORS_MEDIUMGREY,
  TICKETSMS_COLORS_WHITE,
} from 'utils/constants/colors'

import { ButtonWhiteBlack } from 'components/Actions/Action'
import Img from 'components/Media/Img'
import { useBackendApiBase } from 'hooks/useEnv'

import BarTimer from '../Tap2GOModal/BarTimer'
import DraggableBox from '../Tap2GOModal/DraggableBox'

interface Props {
  onClose(checkedAt?: string | null): void
  setCheckedAt?(checkedAt?: string | null): void
  ticketsData: IGoodServiceTicketData[]
  initialUuid: string
  tap2GoStatus: Tap2GOStatus
  setTap2GoStatus(status: Tap2GOStatus): void
}

const Tap2GoMultipleModal: React.FC<Props> = (props) => {
  const { onClose, setCheckedAt, ticketsData, initialUuid, tap2GoStatus, setTap2GoStatus } = props

  const { t } = useTranslation()
  const backendApiBase = useBackendApiBase()

  const [initialTime, setInitialTime] = useState<number>(0)
  const [currentTime, setCurrentTime] = useState<number>(0)

  const [currentTryout, setCurrentTryout] = useState<number>(0)

  const [currentUuid, setCurrentUuid] = useState<string>(initialUuid)

  const [tap2GoRequestData, setTap2GoRequestData] = useState<ITap2GOData | null>(null)
  const [tap2GoFailureMsg, setTap2GoFailureMsg] = useState<string>('')

  const [tap2GoSuccessCode, setTap2GoSuccessCode] = useState<string>('')

  const isTicketDrink = ticketsData[0]?.event?.policy?.code == GoodTicketCode.drink
  const [ticketsGroupedByNotes, setTicketsGroupedByNotes] = useState<Array<IGoodServiceTicketData[]>>([])

  useEffect(() => {
    if (!backendApiBase || tap2GoRequestData) return

    if (currentTryout >= 5) {
      onClose()
    }

    axios
      .post(`${backendApiBase}${ApiEndpoint.vault.requestBulk()}`, {
        secrets: [...ticketsData.map((x) => x.secret)],
        requestCode: currentUuid,
        eventId: ticketsData[0]?.event.id,
      })
      .then((response) => {
        // TODO set response data
        setTap2GoRequestData(response.data.data)

        setCurrentTryout((prevTryout) => {
          return prevTryout + 1
        })
      })
      .catch((error) => {
        setTap2GoStatus(Tap2GOStatus.failure)

        if (!navigator.onLine) {
          setTap2GoFailureMsg(String(t('goodsAndServices:tap2go_errors_offline')))
        } else {
          setTap2GoFailureMsg(error.response.data.error)
        }
      })
  }, [backendApiBase, currentTryout, tap2GoRequestData])

  useEffect(() => {
    if (ticketsData) {
      setTicketsGroupedByNotes(
        Object.values(
          ObjectGroupBy(ticketsData, (elem) => {
            return elem.notes
          })
        ).filter((x) => x !== undefined)
      )
    }
  }, [ticketsData])

  useEffect(() => {
    if (tap2GoStatus == Tap2GOStatus.start) {
      const timerInterval = setInterval(() => {
        setCurrentTime((prevTime) => {
          if (prevTime === 0) {
            setTap2GoStatus(Tap2GOStatus.request)
            return 0
          } else {
            return prevTime - 1
          }
        })
      }, 1000)

      return () => clearInterval(timerInterval)
    }
  }, [tap2GoStatus])

  const getDifferenceInSeconds = (from: number, to: number): number => {
    const isMilliseconds = from > 1e12 || to > 1e12
    if (isMilliseconds) {
      return Math.floor((to - from) / 1000)
    }
    return to - from
  }

  useEffect(() => {
    if (!tap2GoRequestData) return

    const initialTime = getDifferenceInSeconds(tap2GoRequestData.otp.validFrom, tap2GoRequestData.otp.validTo)

    setInitialTime(initialTime)
    setCurrentTime(initialTime - ((tap2GoRequestData.otp.timeElapsed / 1000) | 0))

    setTap2GoStatus(Tap2GOStatus.start)
  }, [tap2GoRequestData])

  useEffect(() => {
    if (tap2GoStatus == Tap2GOStatus.request) {
      setTap2GoRequestData(null)
    }
  }, [tap2GoStatus])

  useEffect(() => {
    if (tap2GoStatus == Tap2GOStatus.check) {
      axios
        .post(`${backendApiBase}${ApiEndpoint.vault.checkBulk()}`, {
          otp: tap2GoRequestData?.otp.value,
          secrets: [...ticketsData.map((x) => x.secret)],
          requestCode: currentUuid,
          eventId: ticketsData[0]?.event.id,
        })
        .then((response) => {
          setTap2GoStatus(Tap2GOStatus.success)
          setTap2GoSuccessCode(String(tap2GoRequestData?.otp.value))

          if (response?.data?.data?.checkedAt?.date_tz && setCheckedAt) {
            setCheckedAt(response?.data?.data?.checkedAt?.date_tz as string)
          }
        })
        .catch((error) => {
          setTap2GoStatus(Tap2GOStatus.failure)

          if (!navigator.onLine) {
            setTap2GoFailureMsg(String(t('goodsAndServices:tap2go_errors_offline')))
          } else {
            setTap2GoFailureMsg(error.response.data.error)
          }
        })
    }
  }, [tap2GoStatus])

  const handleCheck = () => {
    setTap2GoStatus(Tap2GOStatus.check)
  }

  return (
    <div className={css.outer_wrapper}>
      <div data-testid={DataTestId.bag.tap2GoModal} className={css.wrapper}>
        <div className={css.header}>
          <div className={css.title}>{t('goodsAndServices:tap2Go')}</div>
          <div
            className={css.close}
            data-testid={DataTestId.bag.btnTap2GoModalClose}
            onClick={() => {
              onClose()
            }}
          >
            <Img src="/icons/close_icon_white_smallcross.svg" />
          </div>
        </div>

        {ticketsGroupedByNotes.map((elem, key) => {
          return (
            <div
              className={classNames(css.ticketInfo, { [css.success]: tap2GoStatus == Tap2GOStatus.success })}
              data-testid={DataTestId.bag.tap2GoRecapBox}
              style={{
                backgroundColor: elem[0].customColor ?? TICKETSMS_COLORS_WHITE,
                border: `1px solid ${elem[0].customColor ?? TICKETSMS_COLORS_LILA}`,
              }}
              key={key}
            >
              <div className={css.left}>
                <Img src={`/goodsandservices/${elem[0]?.event?.policy?.code}-icon-valid.svg`} />
              </div>
              <div className={css.right}>
                <div
                  data-testid={DataTestId.bag.tap2GoRecapElemCount}
                  className={css.title}
                >{`${elem.length}x ${elem[0]?.notes}`}</div>
                <div
                  className={css.price}
                >{`${elem[0]?.totalPrice?.formatted.replace(elem[0]?.totalPrice?.symbol, '').replace('.', ',')} ${elem[0]?.totalPrice?.symbol}`}</div>
              </div>
            </div>
          )
        })}

        {[Tap2GOStatus.start, Tap2GOStatus.check].includes(tap2GoStatus) && tap2GoRequestData && (
          <>
            <div className={css.codeBox}>
              <div className={css.code_header}>{t('goodsAndServices:your_code')}</div>
              <div data-testid={DataTestId.bag.otpValue} className={css.code_number}>
                {tap2GoRequestData?.otp.value}
              </div>

              <div className={css.timer}>
                <BarTimer initialTime={initialTime} currentTime={currentTime} />
              </div>

              <DraggableBox
                isTicketDrink={isTicketDrink}
                onDragComplete={handleCheck}
                totalTickets={ticketsData.length}
              />
            </div>
          </>
        )}

        {[Tap2GOStatus.start, Tap2GOStatus.check, Tap2GOStatus.request].includes(tap2GoStatus) && (
          <>
            <div className={css.warning}>
              <div className={css.title}>{t('goodsAndServices:show_generic')}</div>
              <div className={css.details}>{t('goodsAndServices:let_generic')}</div>
            </div>
          </>
        )}

        {tap2GoStatus == Tap2GOStatus.request && (
          <>
            <Skeleton
              className={css.content_skeleton}
              baseColor={TICKETSMS_COLORS_MEDIUMGREY}
              borderRadius={16}
              highlightColor={TICKETSMS_COLORS_LIGHTGREY}
              height={345}
              style={{ marginTop: 8 }}
            />
          </>
        )}

        {tap2GoStatus == Tap2GOStatus.success && (
          <>
            <div data-testid={DataTestId.bag.tap2GoSuccessBox} className={css.successBox}>
              <div className={css.codeRecap}>
                <div className={css.headerBox}>{t('goodsAndServices:code')}</div>
                <div className={css.number}>{`${tap2GoSuccessCode}`}</div>
              </div>

              <Img
                className={css.image}
                src={`/goodsandservices/tap2go-${ticketsData[0]?.event?.policy?.code}-success.svg`}
              />

              <div className={css.title}>{t('goodsAndServices:thanks_for_using')}</div>

              <div className={css.details}>
                {t(
                  ticketsData.length > 1
                    ? 'goodsAndServices:generic_card_valid_msg_plural'
                    : 'goodsAndServices:generic_card_valid_msg'
                )}
              </div>
            </div>
          </>
        )}

        {tap2GoStatus == Tap2GOStatus.failure && (
          <>
            <div className={css.failureBox}>
              <Img
                className={css.image}
                src={`/goodsandservices/tap2go-${ticketsData[0]?.event?.policy?.code}-error.svg`}
              />

              <ButtonWhiteBlack
                className={css.button}
                onClick={() => {
                  if (tap2GoRequestData) {
                    // set current UUID
                    setCurrentUuid(uuidv4())
                    setTap2GoStatus(Tap2GOStatus.request)
                  } else {
                    window.location.reload()
                    onClose()
                  }
                }}
              >
                {tap2GoRequestData && t('goodsAndServices:button_try_again')}
                {!tap2GoRequestData && t('goodsAndServices:button_close')}
              </ButtonWhiteBlack>

              <div className={css.warning}>
                <div className={css.inner}>
                  <div className={css.title}>{t('goodsAndServices:something_went_wrong')}</div>
                  <div className={css.details}>{tap2GoFailureMsg}</div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Tap2GoMultipleModal
